/**
 * Shared formatting utility functions
 */

/**
 * Format a date string to a localized display format
 */
export const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/** Display API leadDate (YYYY-MM-DD) without timezone shift. */
export const formatIsoDateForDisplay = (isoDate: string | undefined | null): string => {
  if (!isoDate?.trim()) return '-';
  const match = isoDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Format a datetime string with time
 */
export const formatDateTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Compact INR for stat cards and tight layouts:
 * - ₹0 – ₹99,999: full amount with Indian grouping
 * - ₹1.00 lac – ₹99.99 lac: lakh notation (2 decimals, truncated)
 * - ₹1.00 Cr+: crore notation (2 decimals, truncated)
 */
const truncateToTwoDecimals = (value: number): string => {
  const truncated = Math.floor(value * 100) / 100;
  return truncated.toFixed(2);
};

export const formatCompactIndianRupee = (
  amount: string | number | undefined | null
): string => {
  if (amount === undefined || amount === null) return '-';
  const value = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(value)) return '-';

  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs < 100_000) {
    const formatted = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(abs);
    return sign ? `-${formatted}` : formatted;
  }

  if (abs < 10_000_000) {
    return `${sign}₹${truncateToTwoDecimals(abs / 100_000)} lac`;
  }

  return `${sign}₹${truncateToTwoDecimals(abs / 10_000_000)} Cr`;
};

/**
 * Format a price value to Indian currency
 */
export const formatPrice = (price: string | number | undefined | null): string => {
  if (price === undefined || price === null) return '-';
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(numPrice)) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numPrice);
};

/**
 * Format a number with Indian locale
 */
export const formatNumber = (num: number | undefined | null): string => {
  if (num === undefined || num === null) return '-';
  return new Intl.NumberFormat('en-IN').format(num);
};

/**
 * Get full name from first, middle, and last name parts
 */
export const getFullName = (
  firstName?: string | null,
  middleName?: string | null,
  lastName?: string | null
): string => {
  const parts = [firstName, middleName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '-';
};

/**
 * Get duration label from months
 */
export const getDurationLabel = (months: number): string => {
  if (months === 1) return '1 Month';
  if (months === 12) return '1 Year';
  if (months > 12 && months % 12 === 0) return `${months / 12} Years`;
  return `${months} Months`;
};

/**
 * Get value from lookup object or return default
 */
export const getLookupValue = (
  item: { name: string } | string | null | undefined,
  defaultValue = '-'
): string => {
  if (!item) return defaultValue;
  if (typeof item === 'string') return item;
  return item.name || defaultValue;
};
